import { getFilterOptionsFromClassifier } from 'components/tables/list-table/filterUtil';
import { MainFilter } from 'components/tables/list-table/ListTableTypes';
import { useLazyGetEmployeesListWithFiltersQuery } from 'store/api/employees';
import { useLazyGetMinOfficesWithFiltersQuery } from 'store/api/offices';
import { useLazyGetMinTeamsListWithFiltersQuery } from 'store/api/teams';
import { ClassifierData } from 'types/classifiers';
import { PortfolioScope } from 'types/portfolio';
import { TeamType } from 'types/teams';
import { t } from 'utils/getTranslations';
import { officeInactiveSuffix } from 'utils/officeOptionRenderer';

const CONTACT_GROUP = 'contacts.contact';
const CONNECTED_CASES_GROUP = 'contacts.filter.connected-cases';
const OTHER_GROUP = 'contacts.other.title';

/**
 * Scopes whose row set is already pinned to a fixed set of relationship owners — the caller's own
 * portfolio, or the colleagues picked in "People". Filtering by RP/RM on top of that can only ever
 * narrow within owners the user already chose, so the two filters are dropped there.
 */
const isOwnerScopedView = (scope: PortfolioScope): boolean =>
    scope === PortfolioScope.MY || scope === PortfolioScope.USERS;

/**
 * Main filter-panel set for the My Portfolio page: the subset of the Contacts filter set that My
 * Portfolio supports. Labels and RSQL selectors are reused verbatim from the Contacts list so
 * behaviour stays identical, only scoped to whichever portfolio scope is active. Must be called during render so `t()` resolves against the current locale.
 * The office filters drop out for a firm with a single active office and for a user without READ.OFFICES, as on the
 * Contacts list.
 */
export const buildPortfolioMainFilters = (
    classifiers: Record<string, ClassifierData>,
    hasLastEffortType: boolean,
    lastEffortTypeName: string,
    scope: PortfolioScope,
    hasSeveralActiveOffices: boolean
): MainFilter[] => [
    // --- Contact ---
    {
        type: 'text',
        label: t('contacts.filter.contact-name'),
        selector: 'name',
        comperator: '=contactnamelk=',
        multiple: true,
        group: CONTACT_GROUP,
    },
    ...(isOwnerScopedView(scope)
        ? []
        : ([
              {
                  type: 'dynamicmultiselect',
                  group: CONTACT_GROUP,
                  label: t('contacts.about.relationship-partner'),
                  selector: 'partners.employee.id',
                  paginated: {
                      fetch: useLazyGetEmployeesListWithFiltersQuery,
                      searchField: 'fullName',
                      getOptionIdentifier: 'id',
                      getOptionLabel: 'name',
                  },
              },
              {
                  type: 'dynamicmultiselect',
                  group: CONTACT_GROUP,
                  label: t('contacts.about.relationship-manager'),
                  selector: 'managers.employee.id',
                  paginated: {
                      fetch: useLazyGetEmployeesListWithFiltersQuery,
                      searchField: 'fullName',
                      getOptionIdentifier: 'id',
                      getOptionLabel: 'name',
                  },
              },
          ] satisfies MainFilter[])),
    {
        type: 'multiselect',
        label: t('contacts.business-area'),
        selector: 'businessAreas.businessAreaCode',
        group: CONTACT_GROUP,
        options: getFilterOptionsFromClassifier(classifiers['BUSINESS_AREA_TYPE']),
    },
    {
        type: 'dynamicmultiselect',
        group: CONTACT_GROUP,
        label: t('contacts.filter.sector-group'),
        tooltip: 'contacts.tooltips.sector-group',
        selector: 'id',
        comperator: '=hasbusinessareasinsectorgroup=',
        paginated: {
            fetch: useLazyGetMinTeamsListWithFiltersQuery,
            searchField: 'name',
            filters: `teamTypeCode==${TeamType.SECTOR_GROUP};isParent==true`,
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    {
        type: 'multiselect',
        label: t('contacts.segment-type'),
        selector: 'segmentTypeCode',
        group: CONTACT_GROUP,
        options: getFilterOptionsFromClassifier(classifiers['SEGMENT_TYPE']),
    },
    {
        type: 'multiselect',
        label: t('contacts.service-level-type'),
        selector: 'serviceLevelTypeCode',
        group: CONTACT_GROUP,
        options: getFilterOptionsFromClassifier(classifiers['SERVICE_LEVEL_TYPE']),
    },
    {
        type: 'multiselect',
        label: t('address-form.country'),
        selector: 'addresses.countryCode',
        group: CONTACT_GROUP,
        tooltip: 'contacts.tooltips.country',
        options: getFilterOptionsFromClassifier(classifiers['COUNTRY']),
    },
    {
        type: 'text',
        label: t('address-form.city'),
        selector: 'addresses.cityName',
        group: CONTACT_GROUP,
        multiple: true,
    },
    ...(hasSeveralActiveOffices
        ? ([
              {
                  type: 'dynamicmultiselect',
                  group: CONTACT_GROUP,
                  label: t('contacts.filter.has-cases-handled-by-office'),
                  selector: 'matters.handledByOffice.id',
                  tooltip: 'contacts.tooltips.has-cases-handled-by-office',
                  paginated: {
                      fetch: useLazyGetMinOfficesWithFiltersQuery,
                      searchField: 'name',
                      getOptionIdentifier: 'id',
                      getOptionLabel: 'name',
                  },
                  getOptionEndAdornment: officeInactiveSuffix,
              },
              {
                  type: 'dynamicmultiselect',
                  group: CONTACT_GROUP,
                  label: t('contacts.filter.no-recent-work-by-office'),
                  selector: 'id',
                  comperator: '=norecentworkbyoffice=',
                  tooltip: 'contacts.tooltips.no-recent-work-by-office',
                  paginated: {
                      fetch: useLazyGetMinOfficesWithFiltersQuery,
                      searchField: 'name',
                      getOptionIdentifier: 'id',
                      getOptionLabel: 'name',
                  },
                  getOptionEndAdornment: officeInactiveSuffix,
              },
              {
                  type: 'dynamicmultiselect',
                  group: CONTACT_GROUP,
                  label: t('contacts.filter.never-served-by-office'),
                  selector: 'id',
                  comperator: '=neverservedbyoffice=',
                  tooltip: 'contacts.tooltips.never-served-by-office',
                  paginated: {
                      fetch: useLazyGetMinOfficesWithFiltersQuery,
                      searchField: 'name',
                      getOptionIdentifier: 'id',
                      getOptionLabel: 'name',
                  },
                  getOptionEndAdornment: officeInactiveSuffix,
              },
          ] satisfies MainFilter[])
        : []),
    {
        type: 'dynamicmultiselect',
        group: CONTACT_GROUP,
        label: t('contacts.filter.has-cases-in-practice-group'),
        selector: 'matters.practiceAreas.practiceArea.teamPracticeAreas.team.id',
        tooltip: 'contacts.tooltips.has-cases-in-practice-group',
        canExclude: true,
        paginated: {
            fetch: useLazyGetMinTeamsListWithFiltersQuery,
            searchField: 'name',
            filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    {
        type: 'dynamicmultiselect',
        group: CONTACT_GROUP,
        label: t('contacts.filter.no-recent-work-by-practice-group'),
        selector: 'id',
        comperator: '=norecentworkbypracticegroup=',
        tooltip: 'contacts.tooltips.no-recent-work-by-practice-group',
        paginated: {
            fetch: useLazyGetMinTeamsListWithFiltersQuery,
            searchField: 'name',
            filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    {
        type: 'dynamicmultiselect',
        group: CONTACT_GROUP,
        label: t('contacts.filter.never-served-by-practice-group'),
        selector: 'id',
        comperator: '=neverservedbypracticegroup=',
        tooltip: 'contacts.tooltips.never-served-by-practice-group',
        paginated: {
            fetch: useLazyGetMinTeamsListWithFiltersQuery,
            searchField: 'name',
            filters: `teamTypeCode==${TeamType.PRACTICE_GROUP};isParent==true`,
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    {
        type: 'multiselect',
        label: t('contacts.related-keywords'),
        selector: 'relatedKeywords.keywordTypeCode',
        group: CONTACT_GROUP,
        tooltip: 'contacts.tooltips.contact-related-keywords-filter',
        canExclude: true,
        options: getFilterOptionsFromClassifier(classifiers['KEYWORD_TYPE']),
    },
    {
        type: 'offset-date-time-range',
        group: CONTACT_GROUP,
        selector: 'effectiveClientSince',
        label: t('contacts.filter.client-since-from'),
        labelTo: t('contacts.filter.client-since-to'),
        tooltip: 'contacts.tooltips.client-since-filter',
        shouldDisplayQuickPick: false,
    },
    {
        type: 'offset-date-time-range',
        group: CONTACT_GROUP,
        selector: 'id',
        comperator: '=caselasttimecarddate=',
        label: t('contacts.case-last-timecard-date-from'),
        labelTo: t('contacts.case-last-timecard-date-to'),
        tooltip: 'contacts.tooltips.case-last-timecard-date-filter',
        shouldDisplayQuickPick: false,
    },
    {
        type: 'offset-date-time-range',
        group: CONTACT_GROUP,
        selector: 'id',
        comperator: '=lastbdeffortdate=',
        label: t('contacts.filter.last-bd-effort-from'),
        labelTo: t('contacts.filter.last-bd-effort-to'),
        tooltip: 'portfolio.tooltips.last-bd-effort-filter',
        shouldDisplayQuickPick: false,
    },
    {
        type: 'date',
        group: CONTACT_GROUP,
        selector: 'id',
        comperator: '=lastbdeffortolderthan=',
        label: t('contacts.filter.last-bd-effort-older-than'),
        tooltip: 'portfolio.tooltips.last-bd-effort-older-than-filter',
    },
    ...(hasLastEffortType
        ? ([
              {
                  type: 'offset-date-time-range',
                  group: CONTACT_GROUP,
                  selector: 'id',
                  comperator: '=lasthcsmeetingdate=',
                  label: t('contacts.filter.last-effort-activity-from', { activityType: lastEffortTypeName }),
                  labelTo: t('contacts.filter.last-effort-activity-to', { activityType: lastEffortTypeName }),
                  tooltip: 'portfolio.tooltips.last-hcs-meeting-filter',
                  tooltipValues: { activityType: lastEffortTypeName },
                  shouldDisplayQuickPick: false,
              },
              {
                  type: 'date',
                  group: CONTACT_GROUP,
                  selector: 'id',
                  comperator: '=lasthcsmeetingolderthan=',
                  label: t('contacts.filter.last-effort-activity-older-than', {
                      activityType: lastEffortTypeName,
                  }),
                  tooltip: 'portfolio.tooltips.last-hcs-meeting-older-than-filter',
                  tooltipValues: { activityType: lastEffortTypeName },
              },
          ] satisfies MainFilter[])
        : []),
    // --- Connected cases ---
    {
        type: 'multiselect',
        label: t('contacts.filter.connected-case-status'),
        group: CONNECTED_CASES_GROUP,
        selector: 'matters.matterStateTypeCode',
        options: getFilterOptionsFromClassifier(classifiers['MATTER_STATE_TYPE']),
    },
    {
        type: 'dynamicmultiselect',
        group: CONNECTED_CASES_GROUP,
        label: t('contacts.filter.case-supervising-partner'),
        selector: 'matters.supervisedByEmployee.id',
        paginated: {
            fetch: useLazyGetEmployeesListWithFiltersQuery,
            searchField: 'fullName',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    {
        type: 'dynamicmultiselect',
        group: CONNECTED_CASES_GROUP,
        label: t('contacts.filter.case-responsible-lawyer'),
        selector: 'matters.responsibleEmployee.id',
        paginated: {
            fetch: useLazyGetEmployeesListWithFiltersQuery,
            searchField: 'fullName',
            getOptionIdentifier: 'id',
            getOptionLabel: 'name',
        },
    },
    // --- Other ---
    {
        type: 'checkboxgroup',
        selector: 'id',
        comperator: '=hasactivecasesnoagreement=',
        group: OTHER_GROUP,
        filters: [
            {
                tooltip: 'contacts.tooltips.active-cases-no-agreement',
                label: t('contacts.filter.active-cases-no-agreement'),
                argument: 'true',
            },
        ],
    },
    {
        type: 'checkboxgroup',
        selector: 'partners.employee.isActive',
        group: OTHER_GROUP,
        filters: [
            {
                tooltip: 'contacts.tooltips.inactive-relationship-partner',
                label: t('contacts.filter.inactive-relationship-partner'),
                argument: 'false',
            },
        ],
    },
    {
        type: 'checkboxgroup',
        selector: 'managers.employee.isActive',
        group: OTHER_GROUP,
        filters: [
            {
                tooltip: 'contacts.tooltips.inactive-relationship-manager',
                label: t('contacts.filter.inactive-relationship-manager'),
                argument: 'false',
            },
        ],
    },
];
